export class InputManager {
    el;
    h;
    slopTouch;
    slopMouse;
    tapMaxMs;
    wheelK;
    tracks = new Map();
    /** True once the current gesture can no longer be a tap. */
    gestureIsDrag = false;
    pinchDist = 0;
    pinchMidX = 0;
    pinchMidY = 0;
    listeners = [];
    /** Counts for tests/diagnostics. */
    taps = 0;
    drags = 0;
    constructor(el, handlers, opts = {}) {
        this.el = el;
        this.h = handlers;
        this.slopTouch = opts.tapSlopTouch ?? 10;
        this.slopMouse = opts.tapSlopMouse ?? 5;
        this.tapMaxMs = opts.tapMaxMs ?? 450;
        this.wheelK = opts.wheelZoomPerPx ?? 0.0015;
        el.style.touchAction = 'none'; // we own every gesture on the canvas: no browser pan/zoom
        this.on(el, 'pointerdown', this.onDown);
        this.on(window, 'pointermove', this.onMove);
        this.on(window, 'pointerup', this.onUp);
        this.on(window, 'pointercancel', this.onCancel);
        this.on(el, 'wheel', this.onWheel, { passive: false });
        this.on(el, 'contextmenu', (e) => e.preventDefault());
        // iOS Safari page-zoom gestures.
        this.on(el, 'gesturestart', (e) => e.preventDefault());
    }
    on(t, type, fn, o) {
        t.addEventListener(type, fn, o);
        this.listeners.push([t, type, fn, o]);
    }
    destroy() {
        for (const [t, type, fn, o] of this.listeners)
            t.removeEventListener(type, fn, o);
        this.listeners.length = 0;
        this.tracks.clear();
    }
    local(e) {
        const r = this.el.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    onDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 1 && e.button !== 2)
            return;
        e.preventDefault();
        const p = this.local(e);
        this.tracks.set(e.pointerId, { id: e.pointerId, x: p.x, y: p.y, startX: p.x, startY: p.y, startT: performance.now(), type: e.pointerType });
        if (this.tracks.size === 1) {
            // Non-primary mouse buttons always pan (right-drag like many sims).
            this.gestureIsDrag = e.pointerType === 'mouse' && e.button !== 0;
        }
        else {
            this.gestureIsDrag = true; // second finger: pinch, never a tap
            this.beginPinch();
        }
    };
    beginPinch() {
        const [a, b] = this.firstTwo();
        if (!a || !b)
            return;
        this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        this.pinchMidX = (a.x + b.x) / 2;
        this.pinchMidY = (a.y + b.y) / 2;
    }
    firstTwo() {
        const it = this.tracks.values();
        return [it.next().value, it.next().value];
    }
    onMove = (e) => {
        const t = this.tracks.get(e.pointerId);
        if (!t) {
            if (e.pointerType === 'mouse' && e.target === this.el) {
                const p = this.local(e);
                this.h.onHover?.(p.x, p.y);
            }
            return;
        }
        const p = this.local(e);
        const dx = p.x - t.x;
        const dy = p.y - t.y;
        t.x = p.x;
        t.y = p.y;
        if (this.tracks.size >= 2) {
            const [a, b] = this.firstTwo();
            if (!a || !b)
                return;
            const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
            const mx = (a.x + b.x) / 2;
            const my = (a.y + b.y) / 2;
            this.h.onPan?.(mx - this.pinchMidX, my - this.pinchMidY);
            this.h.onZoom?.(dist / this.pinchDist, mx, my);
            this.pinchDist = dist;
            this.pinchMidX = mx;
            this.pinchMidY = my;
            return;
        }
        if (!this.gestureIsDrag) {
            const slop = t.type === 'mouse' ? this.slopMouse : this.slopTouch;
            if (Math.hypot(p.x - t.startX, p.y - t.startY) > slop) {
                this.gestureIsDrag = true;
                this.drags++;
                // Apply the movement accumulated inside the slop so the map does not lag the finger.
                this.h.onPan?.(p.x - t.startX, p.y - t.startY);
            }
            return;
        }
        this.h.onPan?.(dx, dy);
    };
    onUp = (e) => {
        const t = this.tracks.get(e.pointerId);
        if (!t)
            return;
        this.tracks.delete(e.pointerId);
        if (this.tracks.size === 1) {
            // Pinch -> one finger left: continue as a drag from its current position (no jump).
            return;
        }
        if (this.tracks.size === 0) {
            const isTap = !this.gestureIsDrag && performance.now() - t.startT <= this.tapMaxMs;
            this.gestureIsDrag = false;
            if (isTap) {
                this.taps++;
                this.h.onTap?.(t.x, t.y, t.type);
            }
        }
    };
    onCancel = (e) => {
        this.tracks.delete(e.pointerId);
        if (this.tracks.size === 0)
            this.gestureIsDrag = false;
    };
    onWheel = (e) => {
        e.preventDefault(); // also blocks browser page zoom on ctrl+wheel (trackpad pinch)
        const p = this.local(e);
        // Normalise line/page deltas to px.
        const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? this.el.clientHeight : 1;
        const k = e.ctrlKey ? this.wheelK * 4 : this.wheelK; // trackpad pinch sends small deltas
        this.h.onZoom?.(Math.exp(-e.deltaY * unit * k), p.x, p.y);
    };
}
